import React, { useState } from 'react';
import {
  Box, Grid, TextField, MenuItem, Button, DialogActions,
  Typography, Divider, InputAdornment, IconButton,
  Switch, FormControlLabel, FormGroup, Checkbox, Stack, Paper
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { normalizeOptions } from './eqpOptions';
import EmpSelectbox from '../common/form/autocomplete/EmpSelectbox';
import EqpDeleteConfirmDialog from './EqpDeleteConfirmDialog';
import EqpStdTimeSection from './EqpStdTimeSection';

// 통신 방식 - MTConnect/OPC UA는 드라이버 준비 중이어도 설정은 미리 할 수 있게 선택은 허용한다
const CONN_TYPE_OPTIONS = [
  { value: 'FOCAS',     label: 'FOCAS' },
  { value: 'FTP',       label: 'FTP' },
  { value: 'MTCONNECT', label: 'MTConnect (드라이버 준비 중)' },
  { value: 'OPCUA',     label: 'OPC UA (드라이버 준비 중)' },
];
const CONN_TYPE_VALUES = CONN_TYPE_OPTIONS.map((o) => o.value);

const DEFAULT_PORT = { FOCAS: 8193, FTP: 21, MTCONNECT: 5000, OPCUA: 4840 };

// 수집 항목 (CSV로 저장)
const COLLECT_ITEM_OPTIONS = [
  { value: 'STATE',   label: '운전상태' },
  { value: 'ALARM',   label: '알람' },
  { value: 'PROGRAM', label: '프로그램' },
  { value: 'PARTS',   label: '가공 수' },
  { value: 'SPINDLE', label: '주축' },
];

const defaultOptionsFor = (connTyp) => {
  switch (connTyp) {
    case 'FOCAS':
      // 2026-10 현장 테스트: 30i 컨트롤러에서 파라미터 6711 을 못 읽어 매크로 #3901 을 기본값으로 둔다
      return { pollStateSec: 5, pollCounterSec: 60, collectItems: 'STATE,ALARM,PROGRAM,PARTS', partCntSrc: 'MACRO:3901' };
    case 'FTP':
      return { remoteDir: '/', pollSec: 600, onlyWhenIdle: true };
    case 'MTCONNECT':
      return { path: '/current', deviceName: '' };
    case 'OPCUA':
      return { endpointPath: '', securityMode: 'None' };
    default:
      return {};
  }
};

const safeParseOptions = (json) => {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
  } catch (e) {
    return {};
  }
};

const newKey = () => `new-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const newChannel = () => ({
  _key: newKey(),
  connUuid: '',
  connTyp: 'FOCAS',
  useYn: 'Y',
  host: '',
  port: DEFAULT_PORT.FOCAS,
  timeoutSec: 5,
  loginId: '',
  loginPw: '',
  loginPwClear: false,
  loginPwSet: false,
  options: defaultOptionsFor('FOCAS'),
});

// 숫자 입력 필드: 빈 문자열이면 undefined로 취급해 min/max 검증을 건너뛴다
const optionalInt = (min, max) =>
  Yup.number()
    .transform((value, orig) => (orig === '' || orig === null ? undefined : value))
    .integer('정수만 입력 가능합니다.')
    .min(min, `${min}~${max} 사이여야 합니다.`)
    .max(max, `${min}~${max} 사이여야 합니다.`)
    .nullable();

const channelSchema = Yup.object({
  connTyp: Yup.string().oneOf(CONN_TYPE_VALUES, '통신 방식이 올바르지 않습니다.'),
  port: optionalInt(1, 65535),
  timeoutSec: optionalInt(1, 3600),
}).test('focas-option-ranges', null, function (channel) {
  if (!channel || channel.connTyp !== 'FOCAS') return true;
  const errors = [];
  const pollStateSec = channel.options?.pollStateSec;
  const pollCounterSec = channel.options?.pollCounterSec;
  if (pollStateSec !== '' && pollStateSec != null && (Number(pollStateSec) < 1 || Number(pollStateSec) > 3600)) {
    errors.push(this.createError({ path: `${this.path}.options.pollStateSec`, message: '1~3600 사이여야 합니다.' }));
  }
  if (pollCounterSec !== '' && pollCounterSec != null && (Number(pollCounterSec) < 10 || Number(pollCounterSec) > 86400)) {
    errors.push(this.createError({ path: `${this.path}.options.pollCounterSec`, message: '10~86400 사이여야 합니다.' }));
  }
  return errors.length === 0 ? true : new Yup.ValidationError(errors);
});

const validationSchema = Yup.object({
  eqpNm: Yup.string().required('장비명은 필수입니다.'),
  connList: Yup.array().of(channelSchema),
}).test('mon-requires-channel', null, function (values) {
  if (values.monYn !== 'Y') return true;

  const connList = values.connList || [];
  const used = connList.filter((c) => c.useYn === 'Y');
  const errors = [];
  if (used.length === 0) {
    errors.push(this.createError({ path: 'monYn', message: '모니터링을 사용하려면 사용 중인 통신 채널이 최소 1개 있어야 합니다.' }));
  } else if (used.some((c) => !c.host) && !values.connUrl) {
    errors.push(this.createError({ path: 'connUrl', message: '채널에 주소를 입력하지 않았다면 장비 IP를 입력해야 합니다.' }));
  }
  return errors.length === 0 ? true : new Yup.ValidationError(errors);
});

const todayStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const toNum = (v) => (v === '' || v === null || v === undefined ? null : Number(v));


const EqpInfoTab = ({
  eqpData, onSave, onClose, onDelete, eqpTypeOptions = [], eqpStatusOptions = [], agentOptions = [],
  canEdit = true,
}) => {
  const isNew = !eqpData;
  const [showPwMap, setShowPwMap] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleDeleteConfirm = async () => {
    setDeleteConfirmOpen(false);
    await onDelete(eqpData.eqpUuid);
  };

  const formik = useFormik({
    initialValues: {
      eqpNm:      eqpData?.eqpNm      || '',
      eqpTyp:     eqpData?.eqpTyp     || '',
      maker:      eqpData?.maker      || '',
      modelNm:    eqpData?.modelNm    || '',
      serialNo:   eqpData?.serialNo   || '',
      // 설치일이 비어 있으면 오늘 날짜로 채운다(장비 찾기로 등록한 장비는 설치일이 없어 저장이 막혔다 - 2026-10-17)
      installDt:  eqpData?.installDt  || todayStr(),
      location:   eqpData?.location   || '',
      mgrUsrUuid: eqpData?.mgrUsrUuid || '',
      connUrl:    eqpData?.connUrl    || '',
      monYn:      eqpData?.monYn      || 'N',
      agentUuid:  eqpData?.agentUuid  || '',
      connList: (eqpData?.connList || []).map((c) => ({
        _key: c.connUuid || newKey(),
        connUuid: c.connUuid || '',
        connTyp: c.connTyp || 'FOCAS',
        useYn: c.useYn || 'Y',
        host: c.host || '',
        port: c.port ?? '',
        timeoutSec: c.timeoutSec ?? '',
        loginId: c.loginId || '',
        loginPw: '', // 서버가 비번을 내려주지 않으므로 항상 빈 값에서 시작
        loginPwClear: false,
        loginPwSet: !!c.loginPwSet,
        options: safeParseOptions(c.optionsJson),
      })),
      rmk: eqpData?.rmk || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const connList = (values.connList || []).map((c, idx) => ({
        connUuid: c.connUuid || null,
        connTyp: c.connTyp,
        useYn: c.useYn,
        host: c.host,
        port: toNum(c.port),
        timeoutSec: toNum(c.timeoutSec),
        loginId: c.loginId,
        loginPw: c.loginPw || '',
        loginPwClear: !!c.loginPwClear,
        optionsJson: JSON.stringify(normalizeOptions(c.options)),
        sortNo: idx,
      }));
      onSave({
        ...values,
        agentUuid: values.agentUuid || null,
        connList,
      });
    },
  });

  const updateChannel = (idx, patch) => {
    const next = formik.values.connList.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    formik.setFieldValue('connList', next);
  };

  const updateChannelOption = (idx, key, value) => {
    const channel = formik.values.connList[idx];
    updateChannel(idx, { options: { ...channel.options, [key]: value } });
  };

  const handleConnTypChange = (idx, newTyp) => {
    const channel = formik.values.connList[idx];
    const defaults = defaultOptionsFor(newTyp);
    // 미리 채워진 값은 유지하고, 새 방식에 필요한 키만 기본값으로 보충한다 (모르는 키도 그대로 둔다)
    const mergedOptions = { ...defaults, ...channel.options };
    // 포트가 비었거나 다른 방식의 기본 포트(예: FOCAS 8193) 그대로면 새 방식의 기본 포트로 바꾼다.
    // 사용자가 직접 바꿔 둔 포트는 유지한다.
    const isDefaultOfAny = Object.values(DEFAULT_PORT).map(String).includes(String(channel.port));
    const nextPort = !channel.port || isDefaultOfAny ? DEFAULT_PORT[newTyp] ?? channel.port : channel.port;
    updateChannel(idx, { connTyp: newTyp, port: nextPort, options: mergedOptions });
  };

  const addChannel = () => {
    formik.setFieldValue('connList', [...formik.values.connList, newChannel()]);
  };

  const removeChannel = (idx) => {
    const next = formik.values.connList.filter((_, i) => i !== idx);
    formik.setFieldValue('connList', next);
  };

  const togglePwVisible = (key) => setShowPwMap((m) => ({ ...m, [key]: !m[key] }));

  const renderCredsFields = (idx, channel) => (
    <>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth label="아이디" size="small"
          value={channel.loginId}
          onChange={(e) => updateChannel(idx, { loginId: e.target.value })}
          disabled={!canEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth label="비밀번호" size="small"
          type={showPwMap[channel._key] ? 'text' : 'password'}
          value={channel.loginPw}
          onChange={(e) => updateChannel(idx, { loginPw: e.target.value, loginPwClear: false })}
          placeholder={channel.loginPwSet ? '저장됨 — 바꿀 때만 입력' : ''}
          disabled={!canEdit}
          helperText={
            channel.loginPwClear
              ? '저장 시 삭제됨'
              : (channel.loginPwSet ? '저장됨 — 바꿀 때만 입력' : '')
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => togglePwVisible(channel._key)} edge="end">
                  {showPwMap[channel._key] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {canEdit && channel.loginPwSet && (
          <Button
            size="small"
            color={channel.loginPwClear ? 'primary' : 'error'}
            onClick={() => updateChannel(idx, { loginPwClear: !channel.loginPwClear })}
          >
            {channel.loginPwClear ? '삭제 취소' : '비밀번호 삭제'}
          </Button>
        )}
      </Grid>
    </>
  );

  const cncInfoLine = (eqpData?.cncSeries || eqpData?.cncVersion)
    ? `CNC 정보: ${eqpData?.cncSeries || ''} ${eqpData?.cncVersion || ''}${eqpData?.cncInfoDt ? ` (${eqpData.cncInfoDt})` : ''}`
    : 'CNC 정보: 에이전트 연결 후 자동 표시';

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>

        {/* ── 기본 정보 ── */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="장비명 *" name="eqpNm" size="small"
            value={formik.values.eqpNm}
            onChange={formik.handleChange}
            error={formik.touched.eqpNm && Boolean(formik.errors.eqpNm)}
            helperText={formik.touched.eqpNm && formik.errors.eqpNm}
            disabled={!canEdit}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select fullWidth label="장비 분류" name="eqpTyp" size="small"
            value={formik.values.eqpTyp}
            onChange={formik.handleChange}
            disabled={!canEdit}
          >
            <MenuItem value="">선택안함</MenuItem>
            {eqpTypeOptions.map(({ slcCd, itemNm }) => (
              <MenuItem key={slcCd} value={slcCd}>{itemNm}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="제조사" name="maker" size="small"
            value={formik.values.maker}
            onChange={formik.handleChange}
            disabled={!canEdit}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="모델명" name="modelNm" size="small"
            value={formik.values.modelNm}
            onChange={formik.handleChange}
            disabled={!canEdit}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="시리얼번호" name="serialNo" size="small"
            value={formik.values.serialNo}
            onChange={formik.handleChange}
            disabled={!canEdit}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="설치일" name="installDt" size="small"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formik.values.installDt}
            onChange={formik.handleChange}
            disabled={!canEdit}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="설치 위치" name="location" size="small"
            value={formik.values.location}
            onChange={formik.handleChange}
            disabled={!canEdit}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <EmpSelectbox
            formik={formik}
            name="mgrUsrUuid"
            label="담당자"
            variant="outlined"
            margin="none"
            disabled={!canEdit}
          />
        </Grid>

        {/* ── 통신 설정 ── */}
        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }}>
            <Typography variant="caption" color="text.secondary">통신 설정</Typography>
          </Divider>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth label="장비 IP" name="connUrl" size="small"
            placeholder="예) 192.168.0.200"
            value={formik.values.connUrl}
            onChange={formik.handleChange}
            error={Boolean(formik.errors.connUrl)}
            helperText={formik.errors.connUrl}
            disabled={!canEdit}
          />
        </Grid>

        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.monYn === 'Y'}
                onChange={(e) => formik.setFieldValue('monYn', e.target.checked ? 'Y' : 'N')}
                disabled={!canEdit}
              />
            }
            label="모니터링 사용"
          />
          {formik.errors.monYn && (
            <Typography variant="caption" color="error">{formik.errors.monYn}</Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select fullWidth label="담당 에이전트" name="agentUuid" size="small"
            value={formik.values.agentUuid}
            onChange={formik.handleChange}
            disabled={!canEdit}
            helperText={
              formik.values.monYn === 'Y' && !formik.values.agentUuid
                ? '에이전트를 지정해야 실제 수집이 시작됩니다'
                : ' '
            }
          >
            <MenuItem value="">지정 안 함</MenuItem>
            {agentOptions.map((a) => (
              <MenuItem key={a.agentUuid} value={a.agentUuid}>{a.agentNm}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">{cncInfoLine}</Typography>
        </Grid>

        {/* ── 통신 채널 목록 ── */}
        {formik.values.connList.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              등록된 통신 채널이 없습니다. 채널을 추가해 FOCAS/FTP 등을 설정하세요.
            </Typography>
          </Grid>
        )}

        {formik.values.connList.map((channel, idx) => {
          const channelErrors = formik.errors.connList?.[idx] || {};
          return (
            <Grid item xs={12} key={channel._key}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select fullWidth label="방식" size="small"
                      value={channel.connTyp}
                      onChange={(e) => handleConnTypChange(idx, e.target.value)}
                      error={Boolean(channelErrors.connTyp)}
                      helperText={channelErrors.connTyp}
                      disabled={!canEdit}
                    >
                      {CONN_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={channel.useYn === 'Y'}
                          onChange={(e) => updateChannel(idx, { useYn: e.target.checked ? 'Y' : 'N' })}
                          disabled={!canEdit}
                        />
                      }
                      label="사용"
                    />
                    {canEdit && (
                      <Button size="small" color="error" onClick={() => removeChannel(idx)}>삭제</Button>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="주소" size="small"
                      placeholder="비우면 장비 IP 사용"
                      value={channel.host}
                      onChange={(e) => updateChannel(idx, { host: e.target.value })}
                      disabled={!canEdit}
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth label="포트" size="small" type="number"
                      value={channel.port}
                      onChange={(e) => updateChannel(idx, { port: e.target.value })}
                      error={Boolean(channelErrors.port)}
                      helperText={channelErrors.port}
                      disabled={!canEdit}
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth label="응답 대기(초)" size="small" type="number"
                      value={channel.timeoutSec}
                      onChange={(e) => updateChannel(idx, { timeoutSec: e.target.value })}
                      error={Boolean(channelErrors.timeoutSec)}
                      helperText={channelErrors.timeoutSec}
                      disabled={!canEdit}
                    />
                  </Grid>

                  {channel.connTyp === 'FOCAS' && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="상태 수집 주기(초)" size="small" type="number"
                          value={channel.options.pollStateSec ?? ''}
                          onChange={(e) => updateChannelOption(idx, 'pollStateSec', e.target.value)}
                          error={Boolean(channelErrors.options?.pollStateSec)}
                          helperText={channelErrors.options?.pollStateSec}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="누적값 수집 주기(초)" size="small" type="number"
                          value={channel.options.pollCounterSec ?? ''}
                          onChange={(e) => updateChannelOption(idx, 'pollCounterSec', e.target.value)}
                          error={Boolean(channelErrors.options?.pollCounterSec)}
                          helperText={channelErrors.options?.pollCounterSec}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">수집 항목</Typography>
                        <FormGroup row>
                          {COLLECT_ITEM_OPTIONS.map(({ value, label }) => {
                            const list = (channel.options.collectItems || '').split(',').filter(Boolean);
                            return (
                              <FormControlLabel
                                key={value}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={list.includes(value)}
                                    onChange={() => {
                                      const next = list.includes(value)
                                        ? list.filter((v) => v !== value)
                                        : [...list, value];
                                      updateChannelOption(idx, 'collectItems', next.join(','));
                                    }}
                                    disabled={!canEdit}
                                  />
                                }
                                label={label}
                              />
                            );
                          })}
                        </FormGroup>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select fullWidth label="가공 수 읽는 위치" size="small"
                          value={channel.options.partCntSrc || 'MACRO:3901'}
                          onChange={(e) => updateChannelOption(idx, 'partCntSrc', e.target.value)}
                          disabled={!canEdit}
                        >
                          <MenuItem value="MACRO:3901">매크로 #3901</MenuItem>
                          <MenuItem value="PARAM:6711">파라미터 6711</MenuItem>
                        </TextField>
                      </Grid>
                    </>
                  )}

                  {channel.connTyp === 'FTP' && (
                    <>
                      {renderCredsFields(idx, channel)}
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth label="원격 경로" size="small"
                          value={channel.options.remoteDir ?? '/'}
                          onChange={(e) => updateChannelOption(idx, 'remoteDir', e.target.value)}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth label="파일 확인 주기(초)" size="small" type="number"
                          value={channel.options.pollSec ?? 600}
                          onChange={(e) => updateChannelOption(idx, 'pollSec', e.target.value)}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={channel.options.onlyWhenIdle !== false}
                              onChange={(e) => updateChannelOption(idx, 'onlyWhenIdle', e.target.checked)}
                              disabled={!canEdit}
                            />
                          }
                          label="가공 중에는 받지 않기"
                        />
                      </Grid>
                    </>
                  )}

                  {channel.connTyp === 'MTCONNECT' && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="경로" size="small"
                          value={channel.options.path ?? '/current'}
                          onChange={(e) => updateChannelOption(idx, 'path', e.target.value)}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="장치명" size="small"
                          value={channel.options.deviceName || ''}
                          onChange={(e) => updateChannelOption(idx, 'deviceName', e.target.value)}
                          disabled={!canEdit}
                        />
                      </Grid>
                    </>
                  )}

                  {channel.connTyp === 'OPCUA' && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="엔드포인트 경로" size="small"
                          value={channel.options.endpointPath || ''}
                          onChange={(e) => updateChannelOption(idx, 'endpointPath', e.target.value)}
                          disabled={!canEdit}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select fullWidth label="보안 모드" size="small"
                          value={channel.options.securityMode || 'None'}
                          onChange={(e) => updateChannelOption(idx, 'securityMode', e.target.value)}
                          disabled={!canEdit}
                        >
                          <MenuItem value="None">None</MenuItem>
                          <MenuItem value="Sign">Sign</MenuItem>
                          <MenuItem value="SignAndEncrypt">SignAndEncrypt</MenuItem>
                        </TextField>
                      </Grid>
                      {renderCredsFields(idx, channel)}
                    </>
                  )}
                </Grid>
              </Paper>
            </Grid>
          );
        })}

        {canEdit && (
          <Grid item xs={12}>
            <Button size="small" variant="outlined" onClick={addChannel}>+ 채널 추가</Button>
          </Grid>
        )}

        {/* ── 기준시간(계획 가동시간) ── 신규 등록 중(eqpUuid 없음)에는 저장할 대상이 없어 숨긴다 */}
        {!isNew && (
          <Grid item xs={12}>
            <EqpStdTimeSection eqpUuid={eqpData?.eqpUuid} canEdit={canEdit} />
          </Grid>
        )}

        {/* ── 비고 ── */}
        <Grid item xs={12}>
          <TextField
            fullWidth label="비고" name="rmk" size="small"
            multiline rows={2}
            value={formik.values.rmk}
            onChange={formik.handleChange}
            disabled={!canEdit}
          />
        </Grid>

        {!canEdit && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              장비관리 쓰기 권한이 없어 조회만 가능합니다.
            </Typography>
          </Grid>
        )}

      </Grid>

      <DialogActions sx={{ px: 0, pt: 2 }}>
        {canEdit && !isNew && onDelete && (
          <Button
            color="error"
            onClick={() => setDeleteConfirmOpen(true)}
            sx={{ mr: 'auto' }}
          >
            삭제
          </Button>
        )}
        <Button onClick={onClose}>{canEdit ? '취소' : '닫기'}</Button>
        {canEdit && (
          <Button type="submit" variant="contained">
            {isNew ? '등록' : '수정'}
          </Button>
        )}
      </DialogActions>

      <EqpDeleteConfirmDialog
        open={deleteConfirmOpen}
        eqpNm={eqpData?.eqpNm}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default EqpInfoTab;
